const MARKETING_STORAGE_KEY = "marketingAttribution";

const UTM_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

const CLICK_ID_PARAM_KEYS = ["sfi_click_id", "gclid", "fbclid"];

const META_SOURCES = ["facebook", "instagram", "whatsapp", "meta"];
const GOOGLE_SOURCES = ["google", "adwords", "gads", "googleads"];
const SIMPLIFI_SOURCES = ["simplifi", "simpli.fi"];

const safeParseJson = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const getLocalMarketingData = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const data = safeParseJson(window.localStorage.getItem(MARKETING_STORAGE_KEY));
  if (!data) {
    return null;
  }
  // Drop any legacy click_id fields; keep sfi_click_id if present.
  let updated = false;
  if (data.click_id) {
    delete data.click_id;
    updated = true;
  }
  if (data.simpli_click_id) {
    delete data.simpli_click_id;
    updated = true;
  }
  if (data.sf_click_id) {
    delete data.sf_click_id;
    updated = true;
  }
  if (updated) {
    window.localStorage.setItem(MARKETING_STORAGE_KEY, JSON.stringify(data));
  }
  return data;
};

const getUtmDataFromParams = (params) => {
  const data = {};

  UTM_PARAM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value && value.trim() !== "") {
      data[key] = value.trim();
    }
  });

  return data;
};

const getNormalizedSource = (utmData = {}) =>
  typeof utmData.utm_source === "string"
    ? utmData.utm_source.trim().toLowerCase()
    : "";

const getClickIdsFromParams = (params) => {
  const clickIds = {};

  CLICK_ID_PARAM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value && value.trim() !== "") {
      clickIds[key] = value.trim();
    }
  });

  return clickIds;
};

const hasAttributionSignals = (utmData = {}, clickIds = {}) =>
  Object.keys(utmData).length > 0 || Object.keys(clickIds).length > 0;

const deriveProvider = (utmData = {}, clickIds = {}, existingProvider = "") => {
  const source = getNormalizedSource(utmData);

  if (clickIds.fbclid) {
    return "meta";
  }
  if (clickIds.gclid) {
    return "google";
  }
  if (clickIds.sfi_click_id) {
    return "simplifi";
  }

  if (META_SOURCES.includes(source)) {
    return "meta";
    }
  if (GOOGLE_SOURCES.includes(source)) {
    return "google";
  }
  if (SIMPLIFI_SOURCES.includes(source)) {
    return "simplifi";
  }

  if (source !== "") {
    return "other";
  }

  return existingProvider || "other";
};

const buildNextBaseData = (existingData = null, shouldResetAttribution = false) => {
  const nextData = existingData ? { ...existingData } : {};

  if (!shouldResetAttribution) {
    return nextData;
  }

  delete nextData.sfi_click_id;
  delete nextData.gclid;
  delete nextData.fbclid;
  delete nextData.utm_source;
  delete nextData.utm_medium;
  delete nextData.utm_campaign;
  delete nextData.utm_term;
  delete nextData.utm_content;

  return nextData;
};

export const captureMarketingAttributionFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search || "");
  const utmData = getUtmDataFromParams(params);
  const clickIds = getClickIdsFromParams(params);
  const existingData = getLocalMarketingData();
  const existingProvider =
    existingData && typeof existingData.provider === "string"
      ? existingData.provider
      : "";
  const derivedProvider = deriveProvider(
    utmData,
    clickIds,
    existingProvider
  );

  const nowIso = new Date().toISOString();
  const landingUrl = window.location.href || "";
  const referrer = document.referrer || "";
  const hasNewSignals = hasAttributionSignals(utmData, clickIds);
  const providerChanged = existingProvider !== "" && existingProvider !== derivedProvider;
  const shouldResetAttribution = hasNewSignals && providerChanged;
  const baseData = buildNextBaseData(existingData, shouldResetAttribution);
  const isFirstTouch = !existingData || !existingData.first_touch_at || shouldResetAttribution;
  const landingChanged = existingData && existingData.landing_url
    ? existingData.landing_url !== landingUrl
    : true;

  const marketingData = {
    ...baseData,
    provider: derivedProvider,
    ...utmData,
    ...clickIds,
    landing_url: shouldResetAttribution
      ? landingUrl
      : (existingData && existingData.landing_url) || landingUrl,
    referrer: shouldResetAttribution
      ? referrer
      : (existingData && existingData.referrer) || referrer,
    first_touch_at: isFirstTouch
      ? nowIso
      : (existingData && existingData.first_touch_at) || nowIso,
    last_touch_at: nowIso,
    _isNewAttribution: isFirstTouch || landingChanged || hasNewSignals,
  };

  window.localStorage.setItem(
    MARKETING_STORAGE_KEY,
    JSON.stringify(marketingData)
  );

  return marketingData;
};

export const getMarketingAttributionData = () => {
  return getLocalMarketingData();
};

export const getAttributionSource = () => {
  const marketingData = getLocalMarketingData();
  const provider =
    marketingData && typeof marketingData.provider === "string"
      ? marketingData.provider.trim().toLowerCase()
      : "";

  return provider || "other";
};

export const isPaidAdsUser = () => {
  const provider = getAttributionSource();
  return provider === "meta" || provider === "google";
};

export const mergeMarketingAttributionInCustomData = (customData = {}) => {
  const marketingData = getMarketingAttributionData();
  if (!marketingData || Object.keys(marketingData).length === 0) {
    return customData || {};
  }

  const nextCustomData =
    customData && typeof customData === "object" ? { ...customData } : {};
  const currentMarketingData =
    nextCustomData.marketing && typeof nextCustomData.marketing === "object"
      ? nextCustomData.marketing
      : {};

  nextCustomData.marketing = {
    ...currentMarketingData,
    ...marketingData,
  };

  return nextCustomData;
};
